import type { TFunction } from 'i18next';

export type PronounSupport = {
  supportsInclusivePronouns?: boolean;
};

export type Pronoun = 'He' | 'She' | 'They' | 'You' | 'Not Provided';

type ExclusivePronoun = Extract<Pronoun, 'He' | 'She'>;
const exclusivePronouns: ExclusivePronoun[] = ['He', 'She'];

export const isExclusivePronoun = (
  pronoun?: Pronoun,
): pronoun is ExclusivePronoun =>
  exclusivePronouns.includes(pronoun as ExclusivePronoun);

export const isPronounSupported = (
  pronoun: Pronoun,
  { supportsInclusivePronouns }: PronounSupport = {
    supportsInclusivePronouns: false,
  },
) => supportsInclusivePronouns || isExclusivePronoun(pronoun);

export const PRONOUN_HE: Pronoun = 'He';
export const PRONOUN_SHE: Pronoun = 'She';
export const PRONOUN_YOU: Pronoun = 'You';
export const NOT_PROVIDED: Pronoun = 'Not Provided';
/**
 * **Warning**: Use `!isExclusivePronoun(pronoun)` instead of equality comparisons with `PRONOUN_THEY`.
 */
export const PRONOUN_THEY: Pronoun = 'They';

const PRONOUN_THEY_UNAVAILABLE = 'THEY_UNAVAILABLE' as const;
const PRONOUN_YOU_UNAVAILABLE = 'YOU_UNAVAILABLE' as const;
const NOT_PROVIDED_UNAVAILABLE = 'NOT_PROVIDED_UNAVAILABLE' as const;

type TextKeysType = {
  SubjectObject: Record<
    | Pronoun
    | typeof PRONOUN_THEY_UNAVAILABLE
    | typeof PRONOUN_YOU_UNAVAILABLE
    | typeof NOT_PROVIDED_UNAVAILABLE,
    string
  >;
};

const textKeys: TextKeysType = {
  SubjectObject: {
    [PRONOUN_HE]: 'ui.event-management.pronouns.he/him',
    [PRONOUN_SHE]: 'ui.event-management.pronouns.she/her',
    [PRONOUN_THEY]: 'ui.event-management.pronouns.they/them',
    [PRONOUN_THEY_UNAVAILABLE]:
      'ui.event-management.pronouns.they/them.unavailable',
    [PRONOUN_YOU]: 'ui.event-management.pronouns.you/your',
    [PRONOUN_YOU_UNAVAILABLE]:
      'ui.event-management.pronouns.you/your.unavailable',
    [NOT_PROVIDED]: 'ui.event-management.pronouns.opted-out',
    [NOT_PROVIDED_UNAVAILABLE]:
      'ui.event-management.pronouns.opted-out.unavailable',
  },
};

/**
 * Get the text key for a given pronoun's subject/object form.
 *
 * e.g. He/Him, She/Her, They/Them
 */
export const getSubjectObjectTextKey = (pronoun: Pronoun) =>
  textKeys.SubjectObject[pronoun];

export type PronounOptions = {
  value: Pronoun;
  label: string;
  disabled?: boolean;
}[];

export const getPronounOptions = (
  t: TFunction,
  supportsInclusivePronouns = false,
): PronounOptions => [
  { value: PRONOUN_HE, label: t(textKeys.SubjectObject[PRONOUN_HE]) },
  { value: PRONOUN_SHE, label: t(textKeys.SubjectObject[PRONOUN_SHE]) },
  {
    value: PRONOUN_THEY,
    label: supportsInclusivePronouns
      ? t(textKeys.SubjectObject[PRONOUN_THEY])
      : t(textKeys.SubjectObject[PRONOUN_THEY_UNAVAILABLE]),
    disabled: !supportsInclusivePronouns,
  },
  {
    value: PRONOUN_YOU,
    label: supportsInclusivePronouns
      ? t(textKeys.SubjectObject[PRONOUN_YOU])
      : t(textKeys.SubjectObject[PRONOUN_YOU_UNAVAILABLE]),
    disabled: !supportsInclusivePronouns,
  },
  {
    value: NOT_PROVIDED,
    label: supportsInclusivePronouns
      ? t(textKeys.SubjectObject[NOT_PROVIDED])
      : t(textKeys.SubjectObject[NOT_PROVIDED_UNAVAILABLE]),
    disabled: !supportsInclusivePronouns,
  },
];
